// ============================================================================
// LoginForm — the real auth entry point (POST /api/auth/login).
// ----------------------------------------------------------------------------
// AUTH LOGIC IS PRESERVED EXACTLY from the original scaffold: controlled
// username/password, submit → useAuth().login(), and the same ApiError status
// mapping (429 → lockedOut, 401 → invalidCredentials, else → genericError, all
// read from the shared common.auth.* keys). On success we navigate to the
// intended destination. This file only adds presentation polish:
//   • a staggered Framer Motion reveal of the card + fields (reduced-motion aware),
//   • a clickable show / hide password toggle layered over the locked TextField,
//   • an animated, accessible error banner (a gentle shake under full motion),
//   • a warm greeting + one-tap "continue" for a probed returning trainer,
//   • a submit button that shows a spinner + disables while in flight.
// Built on the locked Pulse design system (Card, TextField, Button, Badge); the
// design system itself is untouched.
// ============================================================================
import { useState, useId, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import type { Variants } from "framer-motion";
import { Button, Card, TextField } from "../../components";
import { useAuth } from "../../auth/AuthContext";
import { ApiError } from "../../api";
import type { LoginSession } from "./useLoginSession";

const EASE = [0.22, 0.61, 0.36, 1] as const;

const stagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07, delayChildren: 0.08 } },
};
const rise: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
};

function EyeIcon({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
      {open ? (
        <>
          <path
            d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
        </>
      ) : (
        <>
          <path
            d="M2.5 12S6 5.5 12 5.5c2 0 3.7.7 5.1 1.6M21.5 12S18 18.5 12 18.5c-2 0-3.7-.7-5.1-1.6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M4 4l16 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </>
      )}
    </svg>
  );
}

function ArrowRight() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
      <path
        d="M5 12h14m-6-6 6 6-6 6"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Spinner() {
  return (
    <span
      className="h-5 w-5 animate-spin rounded-full border-[2.5px] border-white/40 border-t-white"
      aria-hidden
    />
  );
}

export function LoginForm({
  session,
  dest,
}: {
  session: LoginSession;
  dest: string;
}) {
  const { t } = useTranslation("login");
  const { login } = useAuth();
  const navigate = useNavigate();
  const reduce = useReducedMotion();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const errorId = useId();

  // --- AUTH LOGIC (preserved verbatim in behaviour) -----------------------
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login({ username, password });
      navigate(dest, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) setError(t("auth.lockedOut", { ns: "common" }));
        else if (err.status === 401)
          setError(t("auth.invalidCredentials", { ns: "common" }));
        else setError(t("auth.genericError", { ns: "common" }));
      } else {
        setError(t("auth.genericError", { ns: "common" }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Resume affordance for a probed returning trainer — pure navigation, no
  // re-auth; their existing session is already valid.
  const onResume = () => navigate(dest, { replace: true });

  return (
    <motion.div
      variants={reduce ? undefined : stagger}
      initial={reduce ? undefined : "hidden"}
      animate={reduce ? undefined : "show"}
      className="w-full max-w-md"
    >
      <motion.div variants={reduce ? undefined : rise}>
        <Card className="space-y-5">
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tightest text-body sm:text-3xl">
              {t("form.title")}
            </h1>
            <p className="mt-1.5 text-body-soft">{t("form.blurb")}</p>
          </div>

          {/* Returning trainer → one-tap resume (optional-first sugar). */}
          {session.isReturning && (
            <motion.div
              initial={reduce ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="flex flex-col gap-3 rounded-md bg-violet-soft p-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <p className="inline-flex items-center gap-2 text-sm font-bold text-ember-600">
                <span className="h-1.5 w-1.5 rounded-full bg-violet" aria-hidden />
                {session.firstName
                  ? t("welcomeBack", { name: session.firstName })
                  : t("welcomeBackGeneric")}
              </p>
              <Button
                size="sm"
                variant="secondary"
                iconRight={<ArrowRight />}
                onClick={onResume}
                className="shrink-0"
              >
                {t("resume")}
              </Button>
            </motion.div>
          )}

          {/* Error banner — assertive for screen readers, gentle shake on motion. */}
          {error && (
            <motion.div
              key={error}
              role="alert"
              id={errorId}
              initial={reduce ? false : { opacity: 0, height: 0 }}
              animate={{
                opacity: 1,
                height: "auto",
                x: reduce ? 0 : [0, -7, 6, -4, 0],
              }}
              transition={{
                opacity: { duration: 0.25 },
                height: { duration: 0.25 },
                x: { duration: 0.4, ease: EASE },
              }}
              className="overflow-hidden"
            >
              <div className="flex items-start gap-2.5 rounded-md bg-coral-soft px-4 py-3 ring-1 ring-coral/30">
                <svg
                  viewBox="0 0 24 24"
                  className="mt-0.5 h-5 w-5 shrink-0 text-volt-600"
                  fill="none"
                  aria-hidden
                >
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                  <path
                    d="M12 7.5v5M12 16h.01"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                  />
                </svg>
                <p className="text-sm font-bold text-volt-600">{error}</p>
              </div>
            </motion.div>
          )}

          <form className="space-y-4" onSubmit={onSubmit} noValidate>
            <motion.div variants={reduce ? undefined : rise}>
              <TextField
                label={t("auth.username", { ns: "common" })}
                name="username"
                autoComplete="username"
                placeholder={t("form.usernamePlaceholder")}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
                disabled={submitting}
                aria-describedby={error ? errorId : undefined}
              />
            </motion.div>

            {/* Password field with a clickable show / hide toggle layered over
                the locked TextField (whose own suffix slot is non-interactive).
                Extra right padding reserves room for the button. */}
            <motion.div variants={reduce ? undefined : rise} className="relative">
              <TextField
                label={t("auth.password", { ns: "common" })}
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                placeholder={t("form.passwordPlaceholder")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={submitting}
                className="pr-12"
                aria-describedby={error ? errorId : undefined}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                disabled={submitting}
                aria-pressed={showPassword}
                aria-label={
                  showPassword ? t("form.hidePassword") : t("form.showPassword")
                }
                className="absolute bottom-0 right-1 grid h-12 w-10 place-items-center rounded-md text-body-faint transition-colors hover:text-body focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt disabled:opacity-50"
              >
                <EyeIcon open={showPassword} />
              </button>
            </motion.div>

            {/* Forgot password? → the public reset-request flow (Wave 8). */}
            <motion.div variants={reduce ? undefined : rise} className="-mt-1 text-right">
              <Link
                to="/forgot"
                className="text-sm font-bold text-body-soft transition-colors hover:text-coral"
              >
                {t("form.forgotPassword")}
              </Link>
            </motion.div>

            <motion.div variants={reduce ? undefined : rise}>
              <Button
                type="submit"
                block
                size="lg"
                disabled={submitting}
                iconRight={submitting ? <Spinner /> : <ArrowRight />}
              >
                {submitting ? t("auth.signingIn", { ns: "common" }) : t("form.submit")}
              </Button>
            </motion.div>
          </form>

          <motion.p
            variants={reduce ? undefined : rise}
            className="text-center text-sm text-body-soft"
          >
            {t("form.noAccount")}{" "}
            <Link
              to="/signup"
              className="font-bold text-coral transition-colors hover:text-volt-600"
            >
              {t("form.signUpLink")}
            </Link>
          </motion.p>

          <motion.p
            variants={reduce ? undefined : rise}
            className="flex items-center justify-center gap-1.5 text-xs font-medium text-body-faint"
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" aria-hidden>
              <rect x="5" y="10.5" width="14" height="9" rx="2.5" stroke="currentColor" strokeWidth="2" />
              <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            {t("form.secureNote")}
          </motion.p>
        </Card>
      </motion.div>
    </motion.div>
  );
}
